import classNames from 'classnames';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useHistory } from 'react-router';

import useClickOutside from '../../hooks/useClickOutside';
import useScrollHeightThreshold from '../../hooks/useScrollHeightThreshold';

import { useUser } from '../../contexts/AuthContext';
import { useEdition } from '../../contexts/EditionContext';
import { useUrlGenerator, useUrlFromHandle } from '../../contexts/RoutesContext';
import { useSearch } from '../../contexts/SearchContext';
import { useMenu, useSearchBar } from '../../contexts/SiteContext';
import BuyTicketsButton from '../buttons/BuyTickets';
import PillButton from '../buttons/Pill';
import CartIcon from '../icons/Cart';
import ProfileIcon from '../icons/Profile';
import SearchIcon from '../icons/Search';
import MainMenu from '../menus/MainMenu';
import PreviewEdition from '../notifications/PreviewEdition';
import FncLogo from '../partials/FncLogo';
import Link from '../partials/Link';
import LocaleSwitcher from '../partials/LocaleSwitcher';
import TopSearch from '../partials/TopSearch';

import styles from '../../../styles/layouts/header.module.scss';

const propTypes = {
    pageLoading: PropTypes.bool,
    minified: PropTypes.bool,
    searchOpen: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    pageLoading: false,
    minified: false,
    searchOpen: false,
    className: null,
};

// @todo might want the English link depending on locale, eh?
const TICKETPRO_CART_URL_FR = 'https://nouveaucinema.ticketpro.ca/fr/cart';
const TICKETPRO_CART_URL_EN = 'https://nouveaucinema.ticketpro.ca/en/cart';

const Header = ({
    pageLoading,
    minified: defaultMinified,
    searchOpen: initialSearchOpen,
    className,
}) => {
    const ref = useRef(null);
    const user = useUser();
    const intl = useIntl();

    const [minified, setMinified] = useState(defaultMinified);
    const route = useUrlGenerator();
    const routeByHandle = useUrlFromHandle();
    const { preview = false, showTicketing = false, ...editionProps } = useEdition() || {};
    const { main, topRight, menuOpen, setMenuOpen } = useMenu();
    const { searchOpen, setSearchOpen } = useSearchBar();

    const scrollComparison = useCallback((y) => y > 100, []);
    const isScrolled = useScrollHeightThreshold(scrollComparison);
    const location = useLocation();
    const history = useHistory();
    const { setValue: setGlobalSearchValue } = useSearch();
    const [searchValue, setSearchValue] = useState();

    const [ticketsMenuItem] = topRight || [];
    const { url: ticketsUrl = null } = ticketsMenuItem || {};

    const onClick = useCallback(() => {
        setMenuOpen(!menuOpen);
        if (menuOpen) setSearchOpen(false); // if nav was open, close search along with it
    }, [menuOpen, setMenuOpen]);

    const onClickSearchBtn = useCallback(() => {
        setSearchOpen(!searchOpen);
    }, [searchOpen, setSearchOpen]);

    const onSearchChange = useCallback(
        (value) => {
            if (isString(value)) {
                setSearchValue(decodeURIComponent(value));
            } else {
                setSearchValue('');
            }
        },
        [setSearchValue],
    );

    const onSearchSubmit = useCallback(
        (search) => {
            document.activeElement.blur();
            const encodedValue = encodeURIComponent(search);
            setGlobalSearchValue(encodedValue);
            setSearchValue(search);
            history.push(`${routeByHandle('search')}?search=${encodedValue}`);
        },
        [history, routeByHandle, setSearchValue, setGlobalSearchValue],
    );

    const onClickOutside = useCallback(() => {
        setSearchOpen(false);
    }, [setSearchOpen]);

    useClickOutside(ref, onClickOutside);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    useEffect(() => {
        if ((isScrolled && defaultMinified === false) || searchOpen) {
            setMinified(true);
        } else if (defaultMinified) {
            setMinified(true);
        } else {
            setMinified(false);
        }
    }, [isScrolled, searchOpen, defaultMinified]);

    useEffect(() => {
        setMenuOpen(false);
        setSearchOpen(false);
    }, [location, setMenuOpen, setSearchOpen, initialSearchOpen]);

    const { locale = null } = intl || {};
    const defaultCartUrl = locale === 'en' ? TICKETPRO_CART_URL_EN : TICKETPRO_CART_URL_FR;
    const pillTicketsUrl = defaultCartUrl || null;

    return (
        <div
            ref={ref}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isMinified]: minified,
                    [styles.isMenuOpen]: menuOpen,
                    [styles.isSearchVisible]: searchOpen,
                },
            ])}
        >
            {preview ? (
                <div className={styles.notification}>
                    <PreviewEdition edition={editionProps} />
                </div>
            ) : null}
            <div className={styles.inner}>
                <Link className={styles.logoLink} href={route('home')}>
                    <FncLogo
                        className={styles.logo}
                        isStacked={!menuOpen && !minified}
                        isInOpenMenu={menuOpen}
                        loading={pageLoading}
                    />
                </Link>
                <div className={styles.navigationWrap}>
                    <div className={styles.navigation}>
                        <MainMenu items={main} className={styles.menu} minified={minified} />
                        {showTicketing && ticketsUrl !== null ? (
                            <BuyTicketsButton
                                href={ticketsUrl}
                                className={styles.buyTicketsBtnLarge}
                            />
                        ) : null}
                    </div>
                </div>
                <div className={styles.actionsMenu}>
                    {showTicketing && ticketsUrl !== null ? (
                        <BuyTicketsButton href={ticketsUrl} className={styles.buyTicketsBtn} />
                    ) : null}
                    <LocaleSwitcher
                        buttonClassName={styles.button}
                        isDark={!minified && !menuOpen}
                        isContrast={!minified && !menuOpen}
                    />
                    {showTicketing && pillTicketsUrl !== null ? (
                        <PillButton
                            className={styles.button}
                            href={pillTicketsUrl}
                            icon={<CartIcon />}
                            isDark={!minified && !menuOpen}
                            isContrast={!minified && !menuOpen}
                            title={intl.formatMessage({
                                defaultMessage: 'View cart on Ticketpro.ca',
                                description: 'Button label (a11y)',
                            })}
                            external
                        />
                    ) : null}
                    <PillButton
                        className={styles.button}
                        icon={<ProfileIcon active={user !== null} />}
                        href={user !== null ? route('account') : route('login')}
                        isDark={!minified && !menuOpen}
                        isContrast={!minified && !menuOpen}
                        title={intl.formatMessage({
                            defaultMessage: 'My account',
                            description: 'Button label (a11y)',
                        })}
                    />
                    <PillButton
                        className={styles.button}
                        onClick={onClickSearchBtn}
                        icon={<SearchIcon />}
                        isDark={!minified && !menuOpen}
                        isContrast={!minified && !menuOpen}
                        title={intl.formatMessage({
                            defaultMessage: 'Search',
                            description: 'Button label (a11y)',
                        })}
                    />
                    <PillButton
                        className={classNames(styles.button, styles.menuBtn, {
                            [styles.isMenuOpen]: menuOpen,
                        })}
                        icon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="40"
                                height="15"
                                viewBox="0 0 40 15"
                            >
                                <line
                                    className={classNames(styles.burger, styles.topBun)}
                                    y1="7.5"
                                    x2="40"
                                    y2="7.5"
                                    fill="none"
                                    stroke="currentColor"
                                />
                                <line
                                    className={classNames(styles.burger, styles.patty)}
                                    y1="7.5"
                                    x2="40"
                                    y2="7.5"
                                    fill="none"
                                    stroke="currentColor"
                                />
                                <line
                                    className={classNames(styles.burger, styles.bottomBun)}
                                    y1="7.5"
                                    x2="40"
                                    y2="7.5"
                                    fill="none"
                                    stroke="currentColor"
                                />
                            </svg>
                        }
                        onClick={onClick}
                        isDark={!minified && !menuOpen}
                        isContrast={!minified && !menuOpen}
                        title={intl.formatMessage({
                            defaultMessage: 'Menu',
                            description: 'Button label (a11y)',
                        })}
                    />
                </div>
            </div>
            <TopSearch
                className={styles.searchContainer}
                searchWrapClassName={styles.searchWrap}
                value={searchValue}
                onSubmit={onSearchSubmit}
                onChange={onSearchChange}
                isFocusable={searchOpen}
            />
        </div>
    );
};

Header.propTypes = propTypes;
Header.defaultProps = defaultProps;

export default Header;
